/**
 * Whether the signed-in user may download (export) the CV a route target
 * addresses, judged the way the API judges it (audit A03/A07).
 *
 * The user's own CVs (`master`, a CV id) can always be downloaded. A delegated
 * target (`grant:<id>` / `access:<id>`) can only when the grant the user holds
 * carries the `export` permission: the API answers `GET /api/cv/.../export/*`
 * with NOT_FOUND otherwise, so the client hides the control instead of
 * offering a download that will fail. The grant list the API returns already
 * carries the EFFECTIVE permissions (role ceiling applied), so this reads it
 * as is. Viewer = view only; editor and admin include export.
 *
 * Pure (no React), shared by the native and web sheets and unit-tested.
 */
import { readGrantTargetId } from './cv-target-route';

export interface DownloadPermissionGrant {
  id: string;
  permissions: readonly string[];
}

export function canDownloadCvTarget(
  target: string | null | undefined,
  receivedGrants: readonly DownloadPermissionGrant[] | undefined,
): boolean {
  const grantId = target ? readGrantTargetId(target) : null;
  if (!grantId) return true;
  // Until the grants are known (loading, or the request failed) the control
  // stays hidden: a download the API will refuse is worse than a short wait.
  if (!receivedGrants) return false;
  const grant = receivedGrants.find((entry) => entry.id === grantId);
  return Boolean(grant?.permissions.includes('export'));
}
